import React, { useState, useRef, useEffect } from 'react';
import { get, noop } from 'lodash-es';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

export const DropDownItem = ({ className, children, onClick, elementId }) => {
  const e2eAttribute = elementId ? { 'data-e2e': `nav-item-${elementId}` } : {};

  return (
    <li className={classNames('ct-drop-down-item', className)} onClick={onClick || undefined} {...e2eAttribute}>
      {children}
    </li>
  );
};

export const DropDownMenu = ({ id, role, className, children }) => {
  return (
    <ul id={id} role={role} className={className}>
      {children}
    </ul>
  );
};

export const DropDown = ({
  children,
  hasCaret = false,
  vertical = true,
  elementId,
  isActive,
  isMenuShown,
  className,
  title,
  ariaControls,
  ariaRole,
  onClosing = noop,
  onOpen = noop,
  onKeyDown = noop,
}) => {
  const subMenuEl = useRef(null);
  const menuCaretButton = useRef(null);
  const { t } = useTranslation();

  // 0 when it's not settled, 1 for right, 2 for left
  const [dropdownOrientation, setDropdownOrientation] = useState(0);

  useEffect(() => {
    const el = subMenuEl.current;
    if (isMenuShown && el && !dropdownOrientation) {
      const { left } = el.getBoundingClientRect();
      const width = el.offsetWidth;
      const container = document.querySelector('.ct-root-wrapper');
      const fitToRight = left + width <= get(container, 'offsetWidth');
      setDropdownOrientation(fitToRight ? 1 : 2);
    }
  }, [isMenuShown, dropdownOrientation]);

  const subMenuStyles = `row ct-drop-down-menu${!isMenuShown ? ' d-none' : ''}${
    dropdownOrientation === 1 ? ' ct-drop-down-menu-right' : dropdownOrientation === 2 ? ' ct-drop-down-menu-left' : ''
  }`;

  const icon = vertical ? faAngleDown : faAngleRight;

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      menuCaretButton.current?.focus();
      onKeyDown(event);
    }
  };

  const handleCaretClick = () => {
    if (isMenuShown) {
      onClosing();
    } else {
      onOpen();
    }
  };

  return (
    children.length > 0 && (
      <span
        role={ariaRole}
        data-e2e={`nav-item-wrapper-${elementId}`}
        className={classNames('ct-drop-down-nav', className)}
        onMouseEnter={onOpen}
        onMouseLeave={onClosing}
        onKeyDown={handleKeyDown}
      >
        <div className="link-wrapper">
          <div className="children">{children[0]}</div>

          {hasCaret && (
            // button was div element
            <button
              aria-label={`${title} ${t('menu')}`}
              aria-controls={ariaControls}
              aria-haspopup="true"
              aria-expanded={isMenuShown}
              ref={menuCaretButton}
              className={classNames('hasCaret', 'link', 'link-button')}
              onClick={handleCaretClick}
            >
              <FontAwesomeIcon
                aria-hidden="true"
                className={classNames({ active: isActive })}
                icon={icon}
              ></FontAwesomeIcon>
            </button>
          )}
        </div>

        {1 in children && (
          <div ref={subMenuEl} className={subMenuStyles}>
            {children[1]}
          </div>
        )}
      </span>
    )
  );
};
